import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

interface AnimatedListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => ComponentChildren;
  keyFn: (item: T, index: number) => string | number;
  /** Max items rendered in DOM at once (viewport lazy loading) */
  batchSize?: number;
  /** ms stagger between each item's animation */
  staggerMs?: number;
  gap?: number;
  direction?: 'column' | 'row';
}

export function AnimatedList<T>({
  items, renderItem, keyFn,
  batchSize = 20, staggerMs = 40,
  gap = 10, direction = 'column',
}: AnimatedListProps<T>) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(batchSize);

  // Load more when sentinel scrolls into view
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisibleCount(c => Math.min(c + batchSize, items.length));
      }
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [items.length, batchSize]);

  // Reset visible count when items change (e.g. filter switch)
  const prevLen = useRef(items.length);
  useEffect(() => {
    if (items.length !== prevLen.current) {
      prevLen.current = items.length;
      setVisibleCount(batchSize);
    }
  }, [items.length, batchSize]);

  const visible = items.slice(0, visibleCount);
  const isColumn = direction === 'column';

  return (
    <div style={{
      display: 'flex',
      flexDirection: isColumn ? 'column' : 'row',
      gap,
    }}>
      {visible.map((item, i) => (
        <ListItem key={keyFn(item, i)} index={i} staggerMs={staggerMs}>
          {renderItem(item, i)}
        </ListItem>
      ))}
      {visibleCount < items.length && <div ref={sentinelRef} style={{ height: 1 }}/>}
    </div>
  );
}

function ListItem({ children, index, staggerMs }: {
  children: ComponentChildren; index: number; staggerMs: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, { threshold: 0.05 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      class={visible ? 'list-item-enter list-item-active' : 'list-item-enter'}
      style={{ animationDelay: `${index * staggerMs}ms` }}
    >
      {children}
    </div>
  );
}
